import { ReactNode } from 'react';
import styled from 'styled-components';

import {
  ColorsV2,
  Divider,
  Flex,
  getToken,
  IconInCircle,
  Pane,
  Text,
  useThemeColors,
  KebabMenu,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

const StyledPane = styled(Pane)`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
  flex-direction: column;
  width: 100%;
`;

import { CenteredSpinner, CopyButton } from '@common';

type ABMManagementCardProps = {
  hasSettingsMenu?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  children: ReactNode;
};

export const ABMManagementCard = ({
  hasSettingsMenu,
  onEdit,
  onRemove,
  children,
}: ABMManagementCardProps) => {
  const { t } = useTranslation('getting-started');
  const [borderColor, iconColor, iconBackgroundColor] = useThemeColors([
    ColorsV2.BORDER,
    ColorsV2.INFO,
    ColorsV2.INFO_LIGHTEST,
  ]);

  return (
    <StyledPane mt="2rem" width="100%">
      {/* Header */}
      <Flex
        p="2.5rem 3rem"
        width="100%"
        flexWrap="wrap"
        justifyContent="space-between"
        gap="1rem">
        <Flex width="100%">
          <IconInCircle
            color={iconColor}
            background={iconBackgroundColor}
            icon="logo-apple"
            customIconSize="2rem"
            size="3.2rem"
          />
          <Text variant="heading-3" ml="1rem">
            {t('abmComponents.title')}
          </Text>
          {hasSettingsMenu && (
            <Flex ml="auto">
              <KebabMenu
                id="abm-settings-menu"
                items={[
                  {
                    label: t('abmComponents.editAbm'),
                    iconBefore: 'pencil',
                    onClick: () => (onEdit ? onEdit() : null),
                  },
                  {
                    label: t('abmComponents.removeAbm'),
                    iconBefore: 'trashcan',
                    onClick: () => (onRemove ? onRemove() : null),
                  },
                ]}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <Divider color={borderColor} />
      {/* Body */}
      {children}
    </StyledPane>
  );
};

export const StepContainer = ({
  children,
  step,
}: {
  children: ReactNode;
  step?: number;
}) => {
  const { t } = useTranslation('getting-started');
  const borderColor = ColorsV2.BORDER;

  return (
    <>
      <Flex width="100%">
        <Flex p="2rem" width="8rem" flexDirection="column" alignItems="end">
          {step && (
            <>
              <Text variant="label-small">{t('abmComponents.step')}</Text>
              <Text variant="heading-2">{step}</Text>
            </>
          )}
        </Flex>
        <Divider vertical color={borderColor} />
        <Flex width="100%" flexWrap="wrap" justifyContent="space-between">
          <Flex gap="1.2rem" p="2rem" flexDirection="column" maxWidth="65rem">
            {children}
          </Flex>
        </Flex>
      </Flex>
      <Divider color={borderColor} />
    </>
  );
};

export const ABMLoadingContainer = () => {
  return (
    <ABMManagementCard>
      <CenteredSpinner height="20rem" alignItems="center" />
    </ABMManagementCard>
  );
};

export type ResellerIdItem = {
  id: string;
  label: string;
};

/**
 * Renders a "white box" list of Electric's ABM reseller ID(s), each with a copy button.
 * Shared between the ABM setup step 3 (US + CA IDs) and the connected-org CA banner (single ID).
 */
export const ResellerIdsList = ({
  resellers,
}: {
  resellers: ResellerIdItem[];
}) => {
  const { t } = useTranslation('getting-started');
  const [backgroundColor] = useThemeColors([ColorsV2.BACKGROUND]);

  return (
    <Flex
      vertical
      rowGap="0.5rem"
      p="1rem 1.5rem"
      backgroundColor={backgroundColor}
      width="fit-content">
      <Text variant="legal">{t('abmSetupForm.step3.resellerIdsLabel')}</Text>
      <Flex flexWrap="wrap" columnGap="2rem" rowGap="0.5rem">
        {resellers.map(reseller => (
          <Flex key={reseller.id} columnGap="0.5rem" alignItems="center">
            <Text variant="label-large">
              {reseller.label}: {reseller.id}
            </Text>
            <CopyButton
              id={`abm-copy-id-button-${reseller.id}`}
              variant="outline"
              textToCopy={reseller.id}>
              {t('abmSetupForm.step3.copyButton')}
            </CopyButton>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
